/**
 * Self-Cert Individual Service
 * Handles data transformation, validation, and API operations for Self-Cert Individual forms
 */

import {
  SelfCertIndividualFormModel,
  SelfCertIndividualResponseModel,
  SelfCertIndividualFieldMapper,
} from "../Models/SelfCertIndividualModels";
import { BASE_URL } from "../App_Settings/app_settings";
import { updateFileData } from "./fileDataService";

export class SelfCertIndividualService {
  /**
   * Convert API response to form model
   */
  static fromApiResponse(apiData: any): SelfCertIndividualFormModel {
    const responseModel = new SelfCertIndividualResponseModel(apiData);

    const formModel = SelfCertIndividualFieldMapper.mapResponseToForm(responseModel);

    return formModel;
  }

  /**
   * Convert form model to API payload
   */
  static toApiPayload(model: SelfCertIndividualFormModel): Record<string, any> {
    return model.toApiPayload();
  }

  /**
   * Generate XML from model using model fields
   */
  static async generateXML(model: SelfCertIndividualFormModel): Promise<string> {
    return await model.toXML();
  }

  /**
   * Validate form model
   */
  static validate(model: SelfCertIndividualFormModel): { isValid: boolean; errors: string[] } {
    return model.validate();
  }

  /**
   * Save form data to API
   */
  static async saveToApi(
    model: SelfCertIndividualFormModel,
    row: any,
    selectedIndex: number = 0
  ): Promise<{ success: boolean; updatedRow?: any; error?: string }> {
    try {
      const payload = this.toApiPayload(model);

      let parsedFormsData;
      try {
        // Parse formsData and check if it's complex structure or simple array
        parsedFormsData = JSON.parse(row.formsData || "[]");

        // Check if it's the complex structure with formsData property and withholdingData
        if (
          parsedFormsData &&
          typeof parsedFormsData === "object" &&
          parsedFormsData.formsData &&
          Array.isArray(parsedFormsData.formsData)
        ) {
          // Find and update the SelfCert Individual form in the complex structure
          let formUpdated = false;

          for (let i = 0; i < parsedFormsData.formsData.length; i++) {
            const formItem = parsedFormsData.formsData[i];
            if (formItem && formItem.data && Array.isArray(formItem.data)) {
              for (let j = 0; j < formItem.data.length; j++) {
                const dataItem = formItem.data[j];
                if (
                  dataItem &&
                  (dataItem.formType === "SelfCertIndividual" ||
                    dataItem.Form_number === "SelfCert-Individual")
                ) {
                  parsedFormsData.formsData[i].data[j] = {
                    ...dataItem,
                    ...payload,
                  };
                  formUpdated = true;
                  break;
                }
              }
              if (formUpdated) break;
            }
          }

          if (!formUpdated) {
            parsedFormsData.formsData.push({
              request_id: "new-selfcert-individual-" + Date.now(),
              status: "success",
              data: [payload],
              reportdata: {
                requestId: "new-selfcert-individual-" + Date.now(),
                subRequestId: "new-selfcert-individual-" + Date.now(),
                taxForms: "SelfCertIndividual",
                type: "extraction",
              },
            });
          }
        } else {
          const formsDataArray = Array.isArray(parsedFormsData) ? parsedFormsData : [];

          // Update the form at the specified index
          if (selectedIndex < formsDataArray.length && formsDataArray[selectedIndex]) {
            formsDataArray[selectedIndex] = {
              ...formsDataArray[selectedIndex],
              ...payload,
            };
          } else {
            while (formsDataArray.length <= selectedIndex) {
              formsDataArray.push({});
            }
            formsDataArray[selectedIndex] = payload;
          }

          parsedFormsData = formsDataArray;
        }
      } catch (error) {
        parsedFormsData = [payload];
      }

      // Store back as stringified structure (preserving complex format if applicable)
      row.formsData = JSON.stringify(parsedFormsData);

      // Send to API using centralized service
      await updateFileData(row);

      return { success: true, updatedRow: row };
    } catch (error) {
      console.error("Error saving Self-Cert Individual form:", error);
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  /**
   * Load form data from API
   */
  static async loadFromApi(
    fileImportId: string,
    selectedIndex: number = 0
  ): Promise<SelfCertIndividualFormModel> {
    try {
      const response = await fetch(
        `${BASE_URL}/api/forms/load?fileImportId=${fileImportId}&selectedIndex=${selectedIndex}&formType=Self-Cert Individual`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiData = await response.json();
      return this.fromApiResponse(apiData);
    } catch (error) {
      console.error("Error loading Self-Cert Individual form:", error);
      // Return empty model on error
      return new SelfCertIndividualFormModel();
    }
  }

  /**
   * Create a copy of the model
   */
  static clone(model: SelfCertIndividualFormModel): SelfCertIndividualFormModel {
    return new SelfCertIndividualFormModel(model.toApiPayload());
  }

  /**
   * Check if model has been modified
   */
  static isModified(
    original: SelfCertIndividualFormModel,
    current: SelfCertIndividualFormModel
  ): boolean {
    const originalPayload = this.toApiPayload(original);
    const currentPayload = this.toApiPayload(current);

    return JSON.stringify(originalPayload) !== JSON.stringify(currentPayload);
  }
}
